import { GroupSecurity } from '@quromedical/fhir-common';
import type { Note } from '@quromedical/models';
import type { ArrayMap } from '@quromedical/utils';
import type { PatientRouteParams } from 'app/routes';
import { List, Section } from 'design-system';
import { useGlobalSearchParams } from 'expo-router';
import { useUserSession } from 'hooks';
import { usePatientTasks, type useTasksProps } from 'hooks/useTasks';
import type React from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import {
  Footer,
  Item,
  type NoteItemData,
  NoteListHeader,
  useAllNotes,
} from './list';

interface NoteListContextData {
  canLoadMore: boolean;
  isLoading: boolean;
  data: Note[];
  loadMore: () => void;
  search: string;
  error?: unknown;
  setSearch: (value: string) => void;
  revalidate: () => void;
}

const NoteListContext = createContext<NoteListContextData>({
  canLoadMore: false,
  isLoading: false,
  data: [],
  loadMore: () => undefined,
  search: '',
  setSearch: () => undefined,
  revalidate: () => undefined,
});

const useNoteList = () => useContext(NoteListContext);

const ListHeaderComponent: React.FC = () => {
  const { patientId } = useGlobalSearchParams<PatientRouteParams>();
  const {
    search,
    setSearch,
    data,
    isLoading,
    revalidate: revalidateNotes,
  } = useNoteList();
  const { invalidate: invalidateTasks } = usePatientTasks({
    subject: patientId,
  });

  const revalidate = useCallback(() => {
    revalidateNotes();
    invalidateTasks();
  }, [revalidateNotes, invalidateTasks]);

  return (
    <NoteListHeader
      loading={isLoading}
      notes={data}
      search={search}
      setSearch={setSearch}
      revalidate={revalidate}
      id={patientId}
    />
  );
};

const ListFooterComponent: React.FC = () => {
  const { canLoadMore, loadMore, error } = useNoteList();
  return (
    <Section hasBottomMargin>
      <Footer onPress={loadMore} canLoadMore={canLoadMore} hasError={!!error} />
    </Section>
  );
};

const createNotesToDataMap =
  (
    patientId: string,
    canEdit: boolean,
    useTaskProps: useTasksProps,
    revalidate: () => Promise<void>,
  ) =>
  (fromPins: boolean): ArrayMap<Note, NoteItemData> =>
  (note) => ({
    ...note,
    revalidate,
    canEdit,
    patientId,
    fromPins,
    useTaskProps,
  });

//TODO: this component is inefficient since there are 2 API calls being made to get notes.
export const PatientNotesScreen: React.FC = () => {
  const { patientId } = useGlobalSearchParams<PatientRouteParams>();
  const session = useUserSession();
  const canEdit = session.hasAny([GroupSecurity.ClinicalImpressionUpdate]);
  const [search, setSearch] = useState('');

  const usePatientTasksProps = usePatientTasks({ subject: patientId });

  const {
    canLoadMore,
    isLoading,
    loadMore,
    data = [],
    pins = [],
    revalidate,
    error,
  } = useAllNotes(patientId, search, usePatientTasksProps.tasks);

  const mapNotesToData = createNotesToDataMap(
    patientId,
    canEdit,
    usePatientTasksProps,
    revalidate,
  );

  const notesData = data.map<NoteItemData>(mapNotesToData(false));
  const pinsData = pins.map<NoteItemData>(mapNotesToData(true));

  const allData = [...pinsData, ...notesData];

  return (
    <NoteListContext.Provider
      value={{
        canLoadMore,
        isLoading: isLoading && usePatientTasksProps.isLoading,
        data,
        loadMore,
        search,
        setSearch,
        revalidate,
        error,
      }}
    >
      <List
        ListHeaderComponent={ListHeaderComponent}
        loading={isLoading}
        spacing="2xs"
        data={allData}
        keyExtractor={(item) =>
          // in order to ensure that items are updated appropriately the key needs to consider the
          // pinned state as well as the data source
          `${item.id}_${item.pinned ? 'pinned' : ''}_${item.fromPins ? 'pin' : ''}`
        }
        Item={Item}
        ListFooterComponent={ListFooterComponent}
      />
    </NoteListContext.Provider>
  );
};
